import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../apiBase';
import {
    getStoredProfiles,
    getCurrentProfile,
    saveProfilesList,
    saveCurrentProfile,
    selectProfileApi
} from '../utils/profileSession';
import { isPlatformAdmin } from '../utils/roles';

const API = getApiBaseUrl();

function profileTypeKey(type) {
    return `accountType.${String(type || '').toLowerCase()}`;
}

export default function ProfileSwitcher({ compact = false }) {
    const { t } = useTranslation('auth');
    const [profiles, setProfiles] = useState(getStoredProfiles);
    const [active, setActive] = useState(getCurrentProfile);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const wrapRef = useRef(null);
    const token = localStorage.getItem('token');

    useEffect(() => {
        if (!token) return;
        axios.get(`${API}/auth/profiles`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then((res) => {
            const list = res.data.profiles || [];
            if (list.length) {
                setProfiles(list);
                saveProfilesList(list);
                const cur = getCurrentProfile();
                const profileId = localStorage.getItem('profileId');
                const match = list.find((p) => p.id === Number(profileId)) || cur || list[0];
                setActive(match);
                saveCurrentProfile(match);
            }
        }).catch(() => {});
    }, [token]);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    if (!token || isPlatformAdmin() || profiles.length <= 1) return null;

    const current = active || profiles[0];

    async function handleSwitch(profileId) {
        if (profileId === current?.id) {
            setOpen(false);
            return;
        }
        setBusy(true);
        try {
            await selectProfileApi(profileId, axios, API);
            window.location.href = '/';
        } catch {
            setBusy(false);
        }
    }

    return (
        <div ref={wrapRef} style={{ position: 'relative' }}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                disabled={busy}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'rgba(255,255,255,0.12)',
                    border: 'none',
                    borderRadius: 8,
                    padding: compact ? '6px 10px' : '8px 12px',
                    color: '#fff',
                    cursor: busy ? 'wait' : 'pointer',
                    maxWidth: compact ? 180 : 220,
                    fontSize: 12,
                    fontWeight: 600
                }}
                title={t('switchProfile')}
            >
                <span>{current?.icon || '👤'}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                    {current?.schoolName || current?.childName || t('switchProfile')}
                </span>
                <span style={{ opacity: 0.7, fontSize: 10 }}>▾</span>
            </button>

            {open ? (
                <div style={{
                    position: 'absolute',
                    right: 0,
                    top: 'calc(100% + 6px)',
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                    minWidth: 260,
                    zIndex: 2000,
                    overflow: 'hidden'
                }}>
                    <div style={{
                        padding: '12px 14px',
                        fontWeight: 700,
                        fontSize: 13,
                        borderBottom: '1px solid #e2e8f0',
                        color: '#1e293b'
                    }}>
                        {t('switchProfile')}
                    </div>
                    {profiles.map((profile) => (
                        <button
                            key={profile.id}
                            type="button"
                            disabled={busy}
                            onClick={() => handleSwitch(profile.id)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                width: '100%',
                                textAlign: 'left',
                                border: 'none',
                                background: profile.id === current?.id ? '#fff5f5' : '#fff',
                                padding: '12px 14px',
                                cursor: busy ? 'wait' : 'pointer',
                                borderBottom: '1px solid #f1f5f9'
                            }}
                        >
                            <span style={{ fontSize: 22 }}>{profile.icon || '👤'}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>
                                    {t(profileTypeKey(profile.type), { defaultValue: profile.type })}
                                </div>
                                <div style={{
                                    fontSize: 12,
                                    color: '#64748b',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {profile.schoolName || profile.childName || '—'}
                                </div>
                            </div>
                            {profile.id === current?.id ? (
                                <span style={{ color: '#EB3333', fontWeight: 800 }}>✓</span>
                            ) : null}
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    );
}
